import { Suspense, type ComponentType, lazy } from 'react';
import * as Sentry from '@sentry/react';
import FeatureCrashFallback from './error-boundaries/FeatureCrashFallback';
import { CircularProgress } from '@mui/material';

/**
 * Loading fallback variants para diferentes contextos
 */
type LoadingVariant = 'circular' | 'linear' | 'minimal' | 'fullscreen';

interface LazyLoadWrapperProps {
  children: React.ReactNode;
  variant?: LoadingVariant;
  minHeight?: string;
}

/**
 * Componentes de fallback para diferentes variantes de loading
 * Centrados globalmente con backdrop blanco semi-transparente
 */
const LoadingFallbacks = {
  circular: (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointerEvents: 'none',
      }}
    >
      <CircularProgress
        size={50}
        sx={{
          color: '#0050da',
        }}
      />
    </div>
  ),
  linear: (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointerEvents: 'none',
      }}
    >
      <CircularProgress
        size={50}
        sx={{
          color: '#0050da',
        }}
      />
      <div style={{ width: '200px', marginTop: '8px' }}>
        <div className="h-1 w-full bg-blue-200 overflow-hidden rounded-full">
          <div className="h-full bg-blue-600 animate-pulse" style={{ width: '60%' }} />
        </div>
      </div>
    </div>
  ),
  minimal: (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        pointerEvents: 'none',
      }}
    >
      <CircularProgress
        size={40}
        sx={{
          color: '#0050da',
        }}
      />
    </div>
  ),
  fullscreen: (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        pointerEvents: 'none',
      }}
    >
      <CircularProgress
        size={60}
        sx={{
          color: '#0050da',
        }}
      />
      <p className="text-gray-700 text-sm font-medium">Cargando módulo...</p>
    </div>
  ),
};

/**
 * Wrapper para componentes con lazy loading
 * Proporciona un fallback de loading mientras se carga el componente
 */
export const LazyLoadWrapper = ({
  children,
  variant = 'circular',
  minHeight = '400px'
}: LazyLoadWrapperProps) => {
  const fallback = LoadingFallbacks[variant];

  return (
    <Sentry.ErrorBoundary fallback={FeatureCrashFallback}>
      <Suspense fallback={fallback}>
        {children}
      </Suspense>
    </Sentry.ErrorBoundary>
  );
};

/**
 * HOC para crear componentes lazy con configuración de retry
 * @param importFn - Función de importación dinámica
 * @param fallbackVariant - Variante del loading fallback
 * @param retries - Número de reintentos en caso de fallo (útil para conexiones inestables)
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  fallbackVariant: LoadingVariant = 'circular',
  retries = 3
) => {
  const LazyComponent = lazy(() => {
    return new Promise<{ default: T }>((resolve, reject) => {
      const attemptImport = (retriesLeft: number) => {
        importFn()
          .then(resolve)
          .catch((error) => {
            if (retriesLeft === 0) {
              reject(error);
              return;
            }
            // Retry con backoff exponencial
            const delay = (retries - retriesLeft + 1) * 1000;
            setTimeout(() => attemptImport(retriesLeft - 1), delay);
          });
      };
      attemptImport(retries);
    });
  });

  return (props: any) => (
    <LazyLoadWrapper variant={fallbackVariant}>
      <LazyComponent {...props} />
    </LazyLoadWrapper>
  );
};

/**
 * Helper para precargar componentes lazy antes de que sean necesarios
 * Útil para precarga en hover o en rutas anticipadas
 */
export const preloadComponent = (importFn: () => Promise<any>) => {
  return importFn();
};
